export type SubtitleDeliveryFormat = 'vtt';
export type SubtitleHandling = 'external-vtt' | 'ass-render' | 'pgs-render' | 'burn-in' | 'unavailable';
export type SubtitleRendererKind = 'jassub' | 'libbitsub' | null;

const EXTERNAL_TEXT_FORMATS = new Set(['vtt', 'webvtt', 'srt', 'subrip']);
const ASS_FORMATS = new Set(['ass', 'ssa']);
const BITMAP_FORMATS = new Set(['pgs', 'pgssub', 'hdmvpgs', 'sup', 'dvdsub', 'dvd_subtitle', 'dvbsub']);

export function normalizeSubtitleFormat(format: string | null | undefined): string {
  return (format || '').trim().toLowerCase().replace(/[\s.-]+/g, '_');
}

function normalizeRenderableFormat(format: string | null | undefined): string {
  const normalized = normalizeSubtitleFormat(format);
  const stripped = normalized.replace(/_?subtitles?$/, '');
  const tokens = stripped.split('_').filter(Boolean);
  if (tokens.some((t) => ASS_FORMATS.has(t))) return 'ass';
  if (tokens.some((t) => t === 'pgs' || t === 'pgssub' || t === 'hdmvpgs') || (tokens.includes('hdmv') && tokens.includes('pgs'))) return 'pgs';
  if (normalized === 'sup' || tokens.includes('sup')) return 'sup';
  if (normalized === 'dvdsub' || tokens.includes('dvdsub')) return 'dvdsub';
  if (normalized === 'dvbsub' || tokens.includes('dvbsub')) return 'dvbsub';
  return stripped;
}

export function getSubtitleDeliveryFormat(format: string): SubtitleDeliveryFormat | null {
  return EXTERNAL_TEXT_FORMATS.has(format) ? 'vtt' : null;
}

export function getSubtitleRendererKind(format: string): SubtitleRendererKind {
  const normalized = normalizeRenderableFormat(format);
  if (ASS_FORMATS.has(normalized)) return 'jassub';
  if (BITMAP_FORMATS.has(normalized) || normalized === 'sup' || normalized === 'dvdsub' || normalized === 'dvbsub') return 'libbitsub';
  return null;
}

export function isAssSubtitlePayload(text: string): boolean {
  const normalized = text.replace(/^﻿/, '').replace(/\r\n?/g, '\n');
  return /^\s*\[Script Info\]\s*$/im.test(normalized) && /^\s*\[Events\]\s*$/im.test(normalized);
}

export function getSubtitleTimeOffsetSec(streamStartSeconds?: number): number {
  return typeof streamStartSeconds === 'number' && Number.isFinite(streamStartSeconds) ? streamStartSeconds : 0;
}

export function getVttCueOffsetMs(streamStartSeconds?: number, fragmentStartSeconds?: number): number {
  const sec = getSubtitleTimeOffsetSec(streamStartSeconds);
  const delta = typeof fragmentStartSeconds === 'number' && fragmentStartSeconds > 0 ? fragmentStartSeconds : 0;
  return sec > 0 ? -(sec - delta) * 1000 : 0;
}

export function applySubtitleOffsetToWebVtt(subtitleText: string, offsetMs: number): string {
  const cleaned = subtitleText.replace(/\{[^}]*\}/g, "");
  if (!Number.isFinite(offsetMs) || Math.abs(offsetMs) < 1) return cleaned;
  const lines = cleaned.split(/\r?\n/);
  const result = lines.map((line) => {
    const match = line.match(/^(\d{1,2}:\d{2}:\d{2}\.\d{3})\s*-->\s*(\d{1,2}:\d{2}:\d{2}\.\d{3})/);
    if (!match) return line;
    const startMs = Math.max(0, timestampToMs(match[1]) + offsetMs);
    const endMs = Math.max(0, timestampToMs(match[2]) + offsetMs);
    return `${msToTimestamp(startMs)} --> ${msToTimestamp(endMs)}`;
  });
  return result.join('\n');
}

function timestampToMs(ts: string): number {
  const [h, m, s] = ts.split(':');
  const [sec, ms] = (s || '0').split('.');
  return parseInt(h || '0', 10) * 3600000 + parseInt(m || '0', 10) * 60000 + parseInt(sec || '0', 10) * 1000 + parseInt((ms || '0').padEnd(3, '0'), 10);
}

function msToTimestamp(ms: number): string {
  const h = Math.floor(ms / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  const millis = Math.floor(ms % 1000);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}.${String(millis).padStart(3, '0')}`;
}
